import {
    builtinControllers,
    DeviceProfile,
    getDeviceProfile,
    getOfflineViewState,
    type OfflineViewState,
    View,
} from "@novorender/api";
import { ObjectDB, type ObjectGroup as SceneObjectGroup, ObjectId } from "@novorender/data-js-api";
import type { Internal } from "@novorender/webgl-api";
import type { Span } from "@opentelemetry/api";
import { getGPUTier } from "detect-gpu";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { dataApi } from "apis/dataV1";
import { useLazyGetModelTreePermissionsQuery, useLazyGetProjectQuery } from "apis/dataV2/dataV2Api";
import { Permission } from "apis/dataV2/permissions";
import { PipelineConfig, ProjectInfo } from "apis/dataV2/projectTypes";
import { Events, track } from "apis/procore";
import { getModelHomeView } from "apis/procore/bimModelHomeView";
// import { getProcoreMetadataFiles } from "apis/procore/bimFiles"; // TODO: Re-enable when backend implements endpoint
import { getProcoreCurrentSceneVersionId } from "apis/procore/bimSceneVersions";
import {
    buildOfflineFasUrl,
    createBimSignedDownloadUrl,
    createBimSignedFileUrl,
    rewriteSignedUrlForProxy,
    sceneBasePathname,
} from "apis/procore/bimSignedDownloadUrl";
import { buildModelRevisionsArgs, selectSceneRevision } from "apis/procore/modelRevisions";
import { useLazyGetModelRevisionsQuery, useLazyGetViewpointQuery } from "apis/procore/procoreApi";
import { ProcoreObjectDB } from "apis/procore/procoreObjectDb";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { StorageKey } from "config/storage";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden } from "contexts/hidden";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import { GroupStatus, ObjectGroup, objectGroupsActions, useDispatchObjectGroups } from "contexts/objectGroups";
import { pickDataAccess } from "dataAccess/pickDataAccess";
import { ensureGuidanceSnapshot } from "features/gpuDetection/ensureGuidanceSnapshot";
import { newDesignLocalStorageKey } from "features/newDesign/utils";
import {
    capturedSceneOpen,
    manifestIdOfUrl,
    registerOpenSceneHostData,
} from "features/offline/hostData/offlineHostData";
import { ofVersion } from "features/offline/hostData/sceneOpen";
import { useSetupVersionComparisonPropertyTree } from "features/propertyTree/hooks/useSetupVersionComparisonPropertyTree";
import { Viewpoint } from "features/viewpoints/types";
import { useSetViewpoint, ViewpointError } from "features/viewpoints/useSetViewpoint";
import { useFillGroupIds } from "hooks/useFillGroupIds";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import {
    explorerActions,
    ProjectType,
    selectBackendProvider,
    selectConfig,
    selectGpuGuidanceEnabled,
    selectHideSpacesRooms,
    selectInitHomeViewpointId,
    selectInitViewpoint,
    selectInitViewpointId,
    selectIsOnline,
    selectProcoreContext,
} from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { CustomProperties } from "types/project";
import { AppObjectDB } from "utils/appObjectDb";
import { VecRGBA } from "utils/color";
import { wrapViewForDebugging } from "utils/debugRenderState";
import { mixpanel } from "utils/mixpanel";
import { ObjectIdSet } from "utils/objectIdSet";
import { discoverPdfObjectIds } from "utils/pdfObjectIds";
import { iterateAsync } from "utils/search";
import { SelectionSet } from "utils/selectionSet";
import { getFromStorage } from "utils/storage";
import { sleep } from "utils/time";

import { getStoredDeviceProfile } from "../debugDeviceProfile";
import { initEngine } from "../engineInit";
import { type EnvironmentPresetId, getEnvironmentPresetBackground, LIGHT_LEGACY_COLOR } from "../environmentPresets";
import { isMobileGpuBudgetDevice } from "../mobileGpuBudget";
import { renderActions } from "../renderSlice";
import { spaceMouseFlightControllerClass } from "../spaceMouseFlightController";
import {
    ensureSpacesRoomsFrozen,
    ensureSpacesRoomsFrozenFromObjectDb,
    invalidateSpacesFreezeApply,
} from "../spacesTransparency";
import { CadCamera, SceneConfig } from "../types";
import { DEFAULT_CAMERA_PINHOLE_FAR, getDefaultCamera, nextFrame } from "../utils";
import { loadCoordinateSystem, loadStandaloneScene, type PendingStandaloneScene } from "./standaloneSceneOpen";
import { useCachedCheckPermissionsQuery } from "./useCachedLazyCheckPermissionsQuery";
import {
    classifyFailureForTelemetry,
    InitCanvasError,
    readForcedSceneError,
    ViewerLoadTelemetrySession,
} from "./viewerLoadError";

async function resolveHomeViewpoint(
    params: {
        companyId: number;
        projectId: number;
        sceneId: string;
        sceneVersionId?: string;
        /** Host-supplied home viewpoint (`InitOptions.homeViewpointId`), used instead of the model home view. */
        homeViewpointId?: string;
    },
    getViewpointQuery: ReturnType<typeof useLazyGetViewpointQuery>[0],
    getModelRevisionsQuery: ReturnType<typeof useLazyGetModelRevisionsQuery>[0],
): Promise<Viewpoint | undefined> {
    try {
        const viewpointId =
            params.homeViewpointId ?? (await resolveModelHomeViewpointId(params, getModelRevisionsQuery));
        if (!viewpointId) {
            return;
        }

        const resp = await getViewpointQuery({
            companyId: params.companyId,
            projectId: params.projectId,
            viewpointId,
        }).unwrap();
        return resp.data.payload;
    } catch (error) {
        console.warn("Failed to resolve home viewpoint fallback:", error);
        telemetry.trackCaughtException({
            source: "render.useHandleInit.resolveHomeViewpoint",
            category: "init",
            error,
        });
    }
}

/** Home viewpoint id configured on the BIM model the open scene version belongs to. */
async function resolveModelHomeViewpointId(
    params: { companyId: number; projectId: number; sceneId: string; sceneVersionId?: string },
    getModelRevisionsQuery: ReturnType<typeof useLazyGetModelRevisionsQuery>[0],
): Promise<string | undefined> {
    const args = buildModelRevisionsArgs(
        { companyId: params.companyId, projectId: params.projectId, sceneVersionId: params.sceneVersionId },
        params.sceneId,
    );
    if (!args) {
        return;
    }
    // preferCacheValue: reuse the shared session cache when populated.
    const revisions = await getModelRevisionsQuery(args, true).unwrap();
    const revision = selectSceneRevision(revisions, {
        sceneVersionId: params.sceneVersionId,
        sceneId: params.sceneId,
    });
    if (!revision?.bim_model_id) {
        return;
    }

    const homeView = await getModelHomeView({
        companyId: params.companyId,
        projectId: params.projectId,
        modelId: revision.bim_model_id,
    });
    return homeView?.bim_viewpoint_id;
}

type SceneDataForInit = Omit<SceneConfig, "db" | "url" | "settings"> & {
    settings: Internal.RenderSettingsExt | undefined;
};

/**
 * Records the outcome of PDF discovery for `sceneId`.
 * @param options.hideSheets Add every discovered sheet to the hidden set. Adding rather than setting
 * keeps whatever an initial viewpoint applied before discovery resolved.
 */
export function settlePdfObjectDiscovery(
    objectIdsPromise: Promise<SelectionSet>,
    {
        sceneId,
        hideSheets,
        dispatchGlobals,
        dispatchHidden,
    }: {
        sceneId: string;
        hideSheets: boolean;
        dispatchGlobals: ReturnType<typeof useExplorerGlobals>["dispatch"];
        dispatchHidden: ReturnType<typeof useDispatchHidden>;
    },
): Promise<void> {
    return objectIdsPromise.then(
        (objectIds) => {
            dispatchGlobals(explorerGlobalsActions.updatePdfObjectDiscovery({ sceneId, status: "ready", objectIds }));
            if (hideSheets) {
                dispatchHidden(hiddenActions.add(objectIds));
            }
        },
        (error) => {
            console.warn("Failed to discover PDF object ids:", error);
            dispatchGlobals(explorerGlobalsActions.updatePdfObjectDiscovery({ sceneId, status: "failed" }));
        },
    );
}

export function useHandleInit({ uxFlavor: forcedUxFlavor }: { uxFlavor?: UxFlavor }) {
    const sceneId = useSceneId();
    const config = useAppSelector(selectConfig);
    const backendProvider = useAppSelector(selectBackendProvider);
    const procoreContext = useAppSelector(selectProcoreContext);
    const gpuGuidanceEnabled = useAppSelector(selectGpuGuidanceEnabled);
    const gpuGuidanceEnabledRef = useRef(gpuGuidanceEnabled);
    gpuGuidanceEnabledRef.current = gpuGuidanceEnabled;
    const hideSpacesRooms = useAppSelector(selectHideSpacesRooms);
    const viewpointId = useAppSelector(selectInitViewpointId);
    const homeViewpointId = useAppSelector(selectInitHomeViewpointId);
    const initViewpoint = useAppSelector(selectInitViewpoint);
    const [getViewpointQuery] = useLazyGetViewpointQuery();
    const [getModelRevisionsQuery] = useLazyGetModelRevisionsQuery();
    const setViewpoint = useSetViewpoint();
    const setViewpointRef = useRef(setViewpoint);
    setViewpointRef.current = setViewpoint;
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const {
        state: { canvas, internalEventEmitter },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const store = useAppStore();

    const [getProject] = useLazyGetProjectQuery();
    const [getModelTreePermissions] = useLazyGetModelTreePermissionsQuery();
    const checkPermissions = useCachedCheckPermissionsQuery();
    const fillGroupIds = useFillGroupIds();
    const setupVersionComparisonPropertyTree = useSetupVersionComparisonPropertyTree();

    const initialized = useRef(false);

    useEffect(() => {
        initView();

        async function initView() {
            if (initialized.current || !canvas) {
                return;
            }

            initialized.current = true;

            telemetry.setGlobalAttributes({
                "scene.id": sceneId,
                // Field names match the tags bim-platform-service puts on its spans
                // (ProjectContextMiddleware), so one Honeycomb filter selects a customer's
                // viewer crashes and their failing API calls alike.
                ...(procoreContext
                    ? { "company.id": procoreContext.companyId, "project.id": procoreContext.projectId }
                    : {}),
            });

            const session = new ViewerLoadTelemetrySession(sceneId);
            const telemetrySpan = session.span;
            dispatch(renderActions.setSceneStatus({ status: AsyncStatus.Loading }));

            // The engine must be picked before `getDeviceProfile` below and before `createView`.
            const [detectedTier] = await Promise.all([
                runPhase(telemetrySpan, "gpu_detection", () => loadDeviceTier()),
                runPhase(telemetrySpan, "engine_init", () => initEngine()),
            ]);
            const uxFlavor = forcedUxFlavor ?? getInitUxFlavor(config.uxFlavor);
            // Do not use detect-gpu `isMobile` here: it treats MacIntel+touch as iPad.
            // iPadOS reports a desktop Macintosh UA, so Procore iOS/Android flavors
            // are the reliable handheld signal in the embed.
            const isMobile = isMobileGpuBudgetDevice(navigator, uxFlavor);
            const deviceAttributes: Record<string, string | number | boolean> = {
                "device.gpu_tier": detectedTier.tier,
                "device.is_mobile": isMobile,
            };
            telemetry.setRenderPerfAttributes({
                "device.gpu_tier": detectedTier.tier,
                "device.is_mobile": isMobile,
            });
            if (detectedTier.gpuName !== undefined) {
                deviceAttributes["device.gpu_name"] = detectedTier.gpuName;
            }
            // Registering globally is what gets these onto the crash beacon, which copies
            // the global attributes. Global attributes are only stamped when a span is
            // created, so the already-open load span still needs them set directly.
            telemetry.setGlobalAttributes(deviceAttributes);
            telemetrySpan?.setAttributes(deviceAttributes);
            const storedDeviceProfile = getStoredDeviceProfile();
            const deviceProfile = storedDeviceProfile
                ? { ...storedDeviceProfile, isMobile }
                : {
                      ...detectedTier,
                      ...(detectedTier.tier >= 0
                          ? getDeviceProfile(detectedTier.tier as DeviceProfile["tier"])
                          : getDeviceProfile(0)),
                      isMobile,
                      autofreezeEnabled: true,
                  };
            const isProcoreDesign =
                uxFlavor === UxFlavor.Procore ||
                uxFlavor === UxFlavor.ProcoreAndroid ||
                uxFlavor === UxFlavor.ProcoreIOS;
            // viewDeviceProfile has only settings relevant for View (not to the bigger app)
            const { autofreezeEnabled = true, ...viewDeviceProfile } = deviceProfile;

            // Procore signed URLs expire after ~24h. The SDK's `urlBuilder` is
            // invoked once per file fetch; we make it async so it can resolve a
            // fresh signed URL (deduped + cached by `createBimSignedFileUrl`)
            // every time, including from inside long-lived web workers.
            //
            // `sceneVersionId` is resolved a few statements below (it can require
            // a network call) so we capture it via a closure variable that's set
            // before `view.loadScene()` triggers any FAS fetch. The view is
            // created lazily; the urlBuilder is not called until `loadScene`.
            let procoreSceneVersionId: string | undefined;
            // Set in the offline branch below, before `view.loadScene()` is called.
            // When true, the urlBuilder must synthesize the FAS URL locally instead
            // of calling `signed_download_urls` (a network POST that fails offline).
            let procoreOffline = false;

            try {
                const forcedError = readForcedSceneError();
                if (forcedError) {
                    throw forcedError;
                }

                const view = await runPhase(telemetrySpan, "view_create", () =>
                    createView(canvas, {
                        deviceProfile: viewDeviceProfile,
                        showWatermark: !isProcoreDesign,
                        invertFlight: isProcoreDesign,
                        urlBuilder:
                            backendProvider === "procore" && procoreContext
                                ? async (_baseUrl, filePath) => {
                                      if (!procoreSceneVersionId) {
                                          throw new Error(
                                              "Procore urlBuilder invoked before sceneVersionId was resolved",
                                          );
                                      }
                                      // Offline: synthesize the FAS URL locally and skip
                                      // `signed_download_urls` (a network POST that fails offline).
                                      // See buildOfflineFasUrl for the OPFS-matching contract.
                                      if (procoreOffline) {
                                          return buildOfflineFasUrl(
                                              {
                                                  companyId: procoreContext.companyId,
                                                  projectId: procoreContext.projectId,
                                                  sceneId,
                                                  sceneVersionId: procoreSceneVersionId,
                                                  sceneSliceId: procoreContext.sceneSliceId,
                                              },
                                              filePath,
                                          );
                                      }
                                      const fresh = await createBimSignedFileUrl(
                                          {
                                              companyId: procoreContext.companyId,
                                              projectId: procoreContext.projectId,
                                              sceneId,
                                              sceneVersionId: procoreSceneVersionId,
                                              sceneSliceId: procoreContext.sceneSliceId,
                                          },
                                          filePath,
                                      );
                                      // `fresh` may be relative (when the dev proxy is active).
                                      return new URL(fresh, window.location.origin);
                                  }
                                : undefined,
                    }).catch((err) => {
                        console.error("Error creating view:", err);
                        canvas.remove();
                        throw new InitCanvasError(err);
                    }),
                );

                let db: ObjectDB | undefined;
                let sceneData: SceneDataForInit;
                let sceneCamera: CadCamera | undefined = undefined;
                let url: URL;
                let parentSceneId: string;
                let isVersionComparison = false;
                let projectV2: ProjectInfo | undefined = undefined;
                let permissions: Permission[] = [];
                let tmZoneForCalc: string | undefined = undefined;
                let availableAndForbidden: { availableIds: SelectionSet; forbiddenIds: Uint32Array } | undefined =
                    undefined;
                let pipelineConfig: PipelineConfig | undefined;
                let availableResourcePaths: string[] = [];
                let viewpointPromise: Promise<Viewpoint | undefined> | undefined = undefined;
                // Passed to view.loadScene. Set in the backend branches below.
                let offlineOnly: boolean | undefined = undefined;
                // Standalone only. The offline state is made before the scene loads, so the scene
                // can open from its capture. The pending scene holds the scene's data until the
                // engine has loaded its version.
                let standaloneOfflineMade: Promise<OfflineViewState | undefined> | undefined;
                let standaloneCaptureFound: Promise<boolean> | undefined;
                let pendingScene: PendingStandaloneScene | undefined;
                let hasCapture = false;
                const isOnline = () => selectIsOnline(store.getState());

                // A constructor-supplied viewpoint takes precedence over any backend-specific
                // viewpoint resolution (procore `viewpointId` fetch or the resolved home view).
                // Works for both backends. Note: this only seeds the *initial* view — the home
                // viewpoint used by `resetView` is left to backend-specific logic below.
                if (initViewpoint) {
                    viewpointPromise = Promise.resolve(initViewpoint);
                }

                const sceneDataLoadPhase = telemetry.startPhase(telemetrySpan, "scene_data_load");
                try {
                    if (backendProvider === "procore") {
                        if (!procoreContext) {
                            throw Object.assign(new Error("Missing Procore context (companyId/projectId)"), {
                                error: "Missing Procore context (companyId/projectId)",
                                statusCode: 400,
                            });
                        }

                        const sceneVersionId =
                            procoreContext.sceneVersionId ??
                            (await getProcoreCurrentSceneVersionId({
                                companyId: procoreContext.companyId,
                                projectId: procoreContext.projectId,
                                sceneId,
                            }));
                        // Make the resolved sceneVersionId visible to the async
                        // urlBuilder closure created at View.create() time.
                        procoreSceneVersionId = sceneVersionId;
                        // Registered here rather than with `scene.id` above because the
                        // version may still need resolving from the API at that point.
                        telemetry.setGlobalAttributes({
                            "scene_version.id": sceneVersionId,
                            // Only for a Snapshot session, so a version-level one is not
                            // stamped with an empty attribute.
                            ...(procoreContext.sceneSliceId ? { "scene_slice.id": procoreContext.sceneSliceId } : {}),
                        });
                        // Publish the resolved id back to the store when the host did not
                        // supply one, so consumers that only read `procoreContext` (e.g. the
                        // scene-backfill recovery poll) see the same version the API calls use.
                        if (!procoreContext.sceneVersionId) {
                            dispatch(explorerActions.setProcoreContext({ ...procoreContext, sceneVersionId }));
                        }
                        let signedUrl: string;
                        // When offline, skip /signed_download_urls and synthesize a FAS URL
                        // from the IDs we have. The SDK falls back to OPFS via requestOfflineFile
                        // (signature/expiry are not validated -- pathname + ?file= match is enough).
                        // Requires the scene was previously synced to OPFS while online.
                        // REACT_APP_SIMULATE_OFFLINE is a dev-only override for when localhost
                        // is reachable but the real API isn't (wifi off + loopback up).
                        const simulateOfflineInDev =
                            import.meta.env.DEV && import.meta.env.REACT_APP_SIMULATE_OFFLINE === "true";
                        const isOffline = !navigator.onLine || simulateOfflineInDev;
                        offlineOnly = isOffline;
                        // Make the urlBuilder synthesize FAS URLs (no signed_download_urls
                        // network call) for every file fetch, not just the entry URL below.
                        procoreOffline = isOffline;

                        if (isOffline) {
                            signedUrl = buildOfflineFasUrl({
                                companyId: procoreContext.companyId,
                                projectId: procoreContext.projectId,
                                sceneId,
                                sceneVersionId,
                                sceneSliceId: procoreContext.sceneSliceId,
                            }).toString();
                            session.stamp("procore.offline_synthesized_signed_url", true);
                            if (simulateOfflineInDev) {
                                session.stamp("procore.simulated_offline_dev", true);
                            }
                        } else {
                            try {
                                signedUrl = await createBimSignedDownloadUrl({
                                    companyId: procoreContext.companyId,
                                    projectId: procoreContext.projectId,
                                    sceneId,
                                    sceneVersionId,
                                    sceneSliceId: procoreContext.sceneSliceId,
                                });
                            } catch (e) {
                                // Mark this as a signed-URL failure for telemetry, then rethrow.
                                session.stamp("procore.signed_url_failed", true);
                                throw e;
                            }
                        }
                        // Always fetch the home viewpoint separately and store it for use in home button.
                        // This runs even when `initViewpoint` is set — the constructor viewpoint only
                        // seeds the initial view; `resetView` continues to apply the home view, which is
                        // the host-supplied `homeViewpointId` when there is one and the model home view
                        // otherwise.
                        const homeViewpointPromise = resolveHomeViewpoint(
                            {
                                companyId: procoreContext.companyId,
                                projectId: procoreContext.projectId,
                                sceneId,
                                sceneVersionId,
                                homeViewpointId: homeViewpointId ?? undefined,
                            },
                            getViewpointQuery,
                            getModelRevisionsQuery,
                        );

                        if (!initViewpoint) {
                            if (viewpointId) {
                                viewpointPromise = getViewpointQuery({
                                    companyId: procoreContext.companyId,
                                    projectId: procoreContext.projectId,
                                    viewpointId: viewpointId,
                                })
                                    .unwrap()
                                    .then((resp) => resp.data.payload);
                            } else {
                                viewpointPromise = homeViewpointPromise;
                            }
                        }

                        // Store home viewpoint for home button to use
                        homeViewpointPromise
                            .then((homeVp) => {
                                dispatch(renderActions.setHomeViewpoint(homeVp));
                            })
                            .catch((e) => {
                                console.warn("Failed to store home viewpoint:", e);
                            });

                        // Rewrite signed URL to use proxy if needed (for local development).
                        // The SDK's async urlBuilder (configured above) is what actually
                        // produces per-request URLs at fetch time; this `rewrittenSignedUrl`
                        // is only used as the `baseSceneUrl` placeholder passed to
                        // `view.loadScene(...)`. It does not drive any FAS network call.
                        const rewrittenSignedUrl = rewriteSignedUrlForProxy(signedUrl);

                        // TODO: Re-enable once backend implements the metadata_files endpoint for future metadata/search features.
                        // void getProcoreMetadataFiles({
                        //     companyId: procoreContext.companyId,
                        //     projectId: procoreContext.projectId,
                        //     sceneId,
                        //     sceneVersionId,
                        // }).catch((e) => console.warn("getProcoreMetadataFiles failed:", e));

                        db = new ProcoreObjectDB(
                            procoreContext.companyId,
                            procoreContext.projectId,
                            sceneId,
                            sceneVersionId,
                            procoreContext.sceneSliceId,
                        );
                        // For Procore backend, create minimal default settings with a default environment
                        sceneData = {
                            title: sceneId,
                            organization: "",
                            tmZone: undefined,
                            projectId: sceneId,
                            objectGroups: [] as SceneObjectGroup[],
                            // Procore supplies no persisted scene custom properties. Forward the
                            // host-resolved rollout values through the synthetic scene config so
                            // initScene can seed the same Redux flags used by other backends.
                            customProperties: {
                                nodeClassSelection: procoreContext.nodeClassSelection ?? false,
                                spaceMouse: procoreContext.spaceMouse ?? false,
                            } as CustomProperties,
                            settings: undefined, // Procore backend doesn't provide settings yet; will use defaults
                        };

                        // NOTE: For Procore, the presigned URL already contains the full path.
                        // We need to strip any path after /versions/<id> since the SDK's urlBuilder
                        // will append file paths via ?file= query parameter

                        // Handle both absolute URLs (production) and relative paths (local dev proxy)
                        if (rewrittenSignedUrl.startsWith("/")) {
                            // Local dev: relative path from proxy rewrite
                            // Construct absolute URL using current origin
                            url = new URL(rewrittenSignedUrl, window.location.origin);
                        } else {
                            // Production: absolute URL
                            url = new URL(rewrittenSignedUrl);
                        }

                        // Ensure the pathname ends at /versions/<sceneVersionId> or /slices/<sceneSliceId> (no trailing slash)
                        url.pathname = sceneBasePathname(url.pathname, {
                            sceneVersionId,
                            sceneSliceId: procoreContext.sceneSliceId,
                        });

                        parentSceneId = sceneId; // Not used by SDK when urlBuilder is provided
                    } else {
                        // Standalone makes the offline state itself, before the scene loads, so the
                        // scene can open from a full sync's capture. It is registered as soon as it
                        // is made, so the groups filled below read their ids from it offline. The
                        // requests wait for it only offline.
                        const offlineStateMade = getOfflineViewState()
                            .catch((e) => {
                                console.warn("getOfflineViewState():", e);
                                return undefined;
                            })
                            .then((offlineState) => {
                                if (offlineState) {
                                    registerOpenSceneHostData({
                                        projectId: sceneId,
                                        offlineState,
                                        sceneKey: { sceneId },
                                        view,
                                        user: () => selectUser(store.getState())?.user,
                                    });
                                }
                                return offlineState;
                            });
                        const captureFound = offlineStateMade.then(
                            async (offlineState) =>
                                offlineState !== undefined &&
                                (await capturedSceneOpen.manifestId(sceneId)) !== undefined,
                        );

                        pendingScene = await loadStandaloneScene({
                            sceneId,
                            hasCapture: captureFound,
                            requests: {
                                sceneConfig: () => dataApi.loadScene(sceneId),
                                project: (args) => getProject(args).unwrap(),
                                modelTreePermissions: (args) => getModelTreePermissions(args, true).unwrap(),
                                isOnline,
                            },
                        });
                        standaloneOfflineMade = offlineStateMade;
                        standaloneCaptureFound = captureFound;
                        const { url: _url, db: _db, ..._sceneData } = pendingScene.sceneConfig;
                        db = _db;
                        sceneData = _sceneData;

                        url = new URL(pendingScene.url);
                        parentSceneId = pendingScene.parentSceneId;
                        isVersionComparison = pendingScene.isVersionComparison;
                        offlineOnly = pendingScene.offlineOnly;
                        session.stamp("offline.offline_only", offlineOnly === true);
                        url.pathname = "";
                    }
                    telemetry.endPhase(sceneDataLoadPhase);
                } catch (e) {
                    telemetry.endPhase(sceneDataLoadPhase, false, e instanceof Error ? e : undefined);
                    throw e;
                }

                telemetry.setGlobalAttributes({
                    "scene.title": sceneData.title,
                    "scene.org": `${sceneData.organization}`,
                });

                const octreeSceneConfig = await runPhase(telemetrySpan, "octree_load", () =>
                    view.loadScene(url, parentSceneId, "index.json", new AbortController().signal, offlineOnly),
                );

                // The version the engine loaded: what a capture must be of to be kept.
                const loadedManifestId = view.offline ? manifestIdOfUrl(view.offline.manifestUrl) : undefined;
                // Only where the scene has an offline section, as `view.offline` says: elsewhere no
                // capture can be of the version loaded.
                const standaloneOffline = view.offline ? await standaloneOfflineMade : undefined;
                if (standaloneOffline) {
                    hasCapture = (await standaloneCaptureFound) ?? false;
                }
                if (pendingScene) {
                    session.stamp("offline.has_capture", hasCapture);
                    const settled = await runPhase(telemetrySpan, "scene_data_settle", () =>
                        pendingScene!.settle(loadedManifestId),
                    );
                    const { url: _url, db: _db, ..._sceneData } = settled.sceneConfig;
                    db = _db;
                    sceneData = _sceneData;
                    sceneCamera = settled.sceneCamera;
                    projectV2 = settled.projectV2;
                    availableResourcePaths = settled.availableResourcePaths;
                }

                const projectIsV2 = Boolean(projectV2);
                const projectId = sceneData.projectId ?? sceneId;

                if (backendProvider !== "procore") {
                    const [tmZoneForCalcRes, permissionsRes, availableAndForbiddenRes, pipelineConfigRes] =
                        await runPhase(telemetrySpan, "permissions_and_metadata", () =>
                            Promise.all([
                                loadCoordinateSystem({
                                    sceneId,
                                    projectV2,
                                    tmZoneV1: sceneData.tmZone,
                                    sceneCenter: octreeSceneConfig.center,
                                    isOnline: isOnline(),
                                    hasCapture,
                                    loadedManifestId,
                                }),
                                (async () => {
                                    const checkPermissionsStart = Date.now();
                                    const scope = {
                                        organizationId: sceneData.organization,
                                        projectId,
                                        viewerSceneId:
                                            sceneId === projectId || isVersionComparison ? undefined : sceneId,
                                    };
                                    try {
                                        return await checkPermissions(
                                            { scope, permissions: Object.values(Permission) },
                                            {
                                                capture: hasCapture
                                                    ? async () =>
                                                          ofVersion(
                                                              await capturedSceneOpen.permissions(sceneId, scope),
                                                              loadedManifestId,
                                                          )
                                                    : undefined,
                                            },
                                        );
                                    } finally {
                                        telemetrySpan?.setAttribute(
                                            "check_permissions_ms",
                                            Date.now() - checkPermissionsStart,
                                        );
                                    }
                                })(),
                                db && availableResourcePaths.length
                                    ? getAvailableAndForbiddenObjectIds(db, availableResourcePaths)
                                    : undefined,
                                (async () => {
                                    const pipelineConfigFetchStart = Date.now();
                                    try {
                                        const resp = await fetch(await view.buildAssetUrlAsync("config.json"));
                                        if (resp.ok) {
                                            return (await resp.json()) as PipelineConfig;
                                        }
                                        console.warn("Failed to fetch pipeline config", resp.statusText);
                                        return undefined;
                                    } catch (error) {
                                        console.error("Failed to fetch pipeline config", error);
                                        return undefined;
                                    } finally {
                                        telemetry.setSpanAttribute(
                                            telemetrySpan,
                                            "pipeline_config_fetch_ms",
                                            Date.now() - pipelineConfigFetchStart,
                                        );
                                    }
                                })(),
                            ]),
                        );

                    tmZoneForCalc = tmZoneForCalcRes;
                    permissions = permissionsRes;
                    availableAndForbidden = availableAndForbiddenRes;
                    pipelineConfig = pipelineConfigRes;
                }

                mixpanel?.register(
                    {
                        "Scene ID": sceneId,
                        "Scene Title": sceneData.title,
                        "Scene Org": sceneData.organization,
                    },
                    { persistent: false },
                );
                mixpanel?.track_pageview({
                    "Scene ID": sceneId,
                    "Scene Title": sceneData.title,
                    "Scene Org": sceneData.organization,
                });
                telemetry.trackPageview({
                    "scene.id": sceneId,
                    "scene.title": sceneData.title,
                    "scene.org": `${sceneData.organization}`,
                });

                const groups: ObjectGroup[] = sceneData.objectGroups
                    .filter((group) => group.id && group.search)
                    .map((group) => ({
                        name: group.name,
                        id: group.id,
                        grouping: group.grouping ?? "",
                        color: group.color ?? ([1, 0, 0, 1] as VecRGBA),
                        opacity: group.opacity ?? 0,
                        search: group.search ?? [],
                        includeDescendants: group.includeDescendants ?? true,
                        status: group.selected
                            ? GroupStatus.Selected
                            : group.hidden
                              ? GroupStatus.Hidden
                              : group.frozen
                                ? GroupStatus.Frozen
                                : GroupStatus.None,
                        // NOTE(OLA): Pass IDs as undefined to be loaded when group is activated.
                        ids: group.ids ? new ObjectIdSet(group.ids) : (undefined as unknown as ObjectIdSet),
                        // There's a high chance that groups hidden in default view won't be immediately needed,
                        // so freeze them by default
                        promotedToFrozen: autofreezeEnabled && group.hidden,
                    }));

                // Standalone's own, made before the load (the view has no url builder there); the
                // view's otherwise, which exists only where the scene has an offline section.
                const offlineWorkerState =
                    standaloneOffline ??
                    (await view.getOfflineViewState().catch((e) => {
                        console.warn("view.getOfflineViewState():", e);
                        return undefined;
                    }));

                const appDb = new AppObjectDB(db!, availableAndForbidden?.availableIds);

                // Started before the initial camera but awaited only where the value is
                // needed: the sparse-scene fallback in `getDefaultCamera` is the one thing on
                // this path that cannot proceed without a backend, and neither a saved
                // `sceneCamera` nor a scene compact enough to frame from its bounding box
                // reaches it. Awaiting here would put the offline-index probe's OPFS walk in
                // front of first paint on every load instead of the few that need the walk.
                // `octreeSceneConfig.id` names the scene's offline directory, while the served
                // BIM index is addressed by the Procore ids — a different id space, so both are
                // passed rather than one standing in for the other.
                const dataAccessPromise = pickDataAccess(
                    appDb,
                    view,
                    offlineWorkerState,
                    procoreContext?.enableBimIndex ?? false,
                    backendProvider === "procore" && procoreContext && procoreSceneVersionId
                        ? {
                              companyId: procoreContext.companyId,
                              projectId: procoreContext.projectId,
                              sceneId,
                              sceneVersionId: procoreSceneVersionId,
                              sceneSliceId: procoreContext.sceneSliceId,
                          }
                        : undefined,
                );
                // The awaits below carry the rejection; this only keeps one that lands while
                // the camera phase is running from being reported as unhandled.
                dataAccessPromise.catch(() => {});

                if (isProcoreDesign) {
                    // Independent of rendering; run alongside data-access setup and
                    // the remaining initialization without delaying the critical path.
                    const objectIdsPromise = dataAccessPromise.then(discoverPdfObjectIds);
                    dispatchGlobals(
                        explorerGlobalsActions.updatePdfObjectDiscovery({
                            sceneId,
                            status: "pending",
                            objectIdsPromise,
                        }),
                    );
                    void settlePdfObjectDiscovery(objectIdsPromise, {
                        sceneId,
                        hideSheets: backendProvider === "procore",
                        dispatchGlobals,
                        dispatchHidden,
                    });
                }

                const getInitialCamera = async () => {
                    return (
                        sceneCamera ??
                        (await getDefaultCamera(
                            dataAccessPromise,
                            octreeSceneConfig.aabb
                                ? {
                                      minX: octreeSceneConfig.aabb.min[0],
                                      minY: octreeSceneConfig.aabb.min[1],
                                      minZ: octreeSceneConfig.aabb.min[2],
                                      maxX: octreeSceneConfig.aabb.max[0],
                                      maxY: octreeSceneConfig.aabb.max[1],
                                      maxZ: octreeSceneConfig.aabb.max[2],
                                  }
                                : projectV2?.bounds
                                  ? {
                                        minX: projectV2.bounds[0],
                                        minY: projectV2.bounds[1],
                                        minZ: 0,
                                        maxX: projectV2.bounds[2],
                                        maxY: projectV2.bounds[3],
                                        maxZ: 0,
                                    }
                                  : undefined,
                        )) ?? {
                            position: view.renderState.camera.position,
                            rotation: view.renderState.camera.rotation,
                            fov: view.renderState.camera.fov,
                            kind: view.renderState.camera.kind,
                        }
                    );
                };

                // Ensure frozen groups are loaded before rendering anything to not even put them into memory
                // (some scenes on some devices may crash upon loading because there's too much data)
                // If there's a viewpoint - group loading will be handled by useSetViewpoint hook.
                const getViewpointOrFillGroupIds = async () => {
                    const viewpoint = await viewpointPromise?.catch((error) => {
                        console.error("Error loading viewpoint:", error);
                        // If failed - proceed with the default home view
                        return undefined;
                    });
                    if (!viewpoint) {
                        await fillGroupIds(
                            groups.filter((g) => g.status !== GroupStatus.None),
                            sceneId,
                        );
                    }

                    return viewpoint;
                };

                const [initialCamera, viewpoint] = await runPhase(telemetrySpan, "camera_and_group_fill", () =>
                    Promise.all([getInitialCamera(), getViewpointOrFillGroupIds()]),
                );

                const fallbackProjectV2Info: ProjectInfo = {
                    id: sceneId,
                    name: "",
                    description: "",
                    viewCount: 0,
                    epsg: "",
                    bounds: [0, 0, 0, 0],
                    permissions,
                    created: "",
                    modified: "",
                };

                // Drop any in-flight freeze apply before initScene clears the list so a
                // previous scene's late ObjectDB/bim result cannot land on this scene's ids.
                invalidateSpacesFreezeApply();
                dispatch(
                    renderActions.initScene({
                        projectType: projectIsV2 ? ProjectType.V2 : ProjectType.V1,
                        projectV2Info: projectV2 ? { ...projectV2, permissions } : fallbackProjectV2Info,
                        pipelineConfig,
                        tmZoneForCalc,
                        sceneData: sceneData as Omit<SceneConfig, "db" | "url">,
                        sceneConfig: octreeSceneConfig,
                        isVersionComparison,
                        initialCamera,
                        deviceProfile,
                        assetsUrl: config.assetsUrl,
                        uxFlavor,
                    }),
                );

                if (backendProvider === "procore" && hideSpacesRooms && procoreContext && procoreSceneVersionId) {
                    // Fire-and-forget: not required to render; never block or fail init.
                    void ensureSpacesRoomsFrozen(
                        {
                            companyId: procoreContext.companyId,
                            projectId: procoreContext.projectId,
                            sceneId,
                            sceneVersionId: procoreSceneVersionId,
                        },
                        dispatch,
                    );
                } else if (backendProvider !== "procore" && hideSpacesRooms) {
                    // Novorender standalone: always on (selector). ObjectDB only — no bim-index.
                    // Same fire-and-forget pattern as PDF discovery above; do not await.
                    void dataAccessPromise
                        .then((access) => ensureSpacesRoomsFrozenFromObjectDb(access, sceneId, dispatch))
                        .catch((e) => {
                            console.warn("Failed to start spaces/rooms ObjectDB freeze:", e);
                        });
                }

                if (isWeakDevice() && getFromStorage(StorageKey.WeakDeviceMessageDismissed) !== "true") {
                    dispatch(explorerActions.setShowWeakDeviceMessage(true));
                }

                dispatch(renderActions.setInitialCamera(initialCamera));

                if (!sceneCamera && octreeSceneConfig.boundingSphere.radius > DEFAULT_CAMERA_PINHOLE_FAR) {
                    // If scene is not configured - camera can be too far by default if project bounds are too big.
                    // Show a snackbar in this case.
                    dispatch(explorerActions.setShowUnusuallyFarObjects(true));
                }

                dispatchObjectGroups(objectGroupsActions.set(groups));
                const primaryHighlightColor =
                    sceneData.customProperties.explorerProjectState?.highlights?.primary?.color ??
                    sceneData.customProperties.highlights?.primary.color;
                const hasLegacyDefaultPrimaryColor =
                    primaryHighlightColor?.[0] === 1 &&
                    primaryHighlightColor?.[1] === 0 &&
                    primaryHighlightColor?.[2] === 0 &&
                    primaryHighlightColor?.[3] === 1;
                const hasLegacyProcorePrimaryColor =
                    primaryHighlightColor?.[0] === 51 / 255 &&
                    primaryHighlightColor?.[1] === 151 / 255 &&
                    primaryHighlightColor?.[2] === 1 &&
                    primaryHighlightColor?.[3] === 1;
                dispatchHighlighted(
                    highlightActions.setColor(
                        isProcoreDesign && (hasLegacyDefaultPrimaryColor || hasLegacyProcorePrimaryColor)
                            ? [24 / 255, 98 / 255, 240 / 255, 1]
                            : (primaryHighlightColor ??
                                  (isProcoreDesign ? [24 / 255, 98 / 255, 240 / 255, 1] : [1, 0, 0, 1])),
                    ),
                );
                dispatchHighlightCollections(
                    highlightCollectionsActions.setColor(
                        HighlightCollection.SecondaryHighlight,
                        sceneData.customProperties.explorerProjectState?.highlights?.secondary?.color ??
                            sceneData.customProperties.highlights?.secondary.color ?? [1, 1, 0, 1],
                    ),
                );

                const resizeObserver = new ResizeObserver((entries) => {
                    for (const entry of entries) {
                        const { width, height } = entry.contentRect;
                        // During orientation changes (especially mobile WebView / Chrome TWA),
                        // contentRect can briefly be 0x0. Feeding that into globals and consumers
                        // causes divide-by-zero and poisons WebGL state.
                        if (width < 1 || height < 1) {
                            continue;
                        }
                        dispatchGlobals(
                            explorerGlobalsActions.update({
                                size: {
                                    width,
                                    height,
                                },
                            }),
                        );
                        internalEventEmitter.emit("appResize", { width, height });
                    }
                });

                resizeObserver.observe(canvas);

                // Publishing globals is where the backend has to be settled, since every
                // consumer reads it from here. By now the probe has had the camera and group
                // fill to run under.
                const dataAccess = await dataAccessPromise;

                if (isProcoreDesign) {
                    // Bake before publishing `view` so useHandleHeadlight's first apply is the
                    // Procore values, not the disabled slice default (non-Procore stays IBL-only).
                    dispatch(
                        renderActions.setHeadlight({
                            enabled: true,
                            intensity: 0.65,
                            innerConeDeg: 0,
                            outerConeDeg: 80,
                            range: 200,
                            color: [1, 1, 0.98],
                        }),
                    );
                }

                dispatchGlobals(
                    explorerGlobalsActions.update({
                        db: appDb,
                        dataAccess,
                        view: view,
                        scene: octreeSceneConfig,
                        offlineWorkerState,
                        availableObjectIds: availableAndForbidden?.availableIds,
                        forbiddenObjectIds: availableAndForbidden?.forbiddenIds,
                    }),
                );

                if (isProcoreDesign) {
                    dispatch(
                        renderActions.setCameraDefaults({
                            pinhole: {
                                usePointerLock: false,
                            },
                            orthographic: {
                                deAcceleration: true,
                                touchRotate: true,
                                mouseRotate: true,
                                topDownSnapToAxis: "north",
                                clipping: { far: 10000 },
                            },
                        }),
                    );

                    // Force generated parametric data enabled to allow snapping (e.g., Point to Point, Coordinates tools)
                    // since Procore UX lacks a user control for this feature.
                    dispatch(renderActions.setGeneratedParametricData({ enabled: true }));

                    // Enable picking semi-transparent (X-Ray) objects. Toon outlines stay on, but not
                    // per object: BM-11787 -- per-object outlines z-fight and chatter on coplanar faces.
                    dispatch(
                        renderActions.setAdvanced({
                            pick: { opacityThreshold: 0.1, xrayPickingEnabled: true },
                            toonOutline: { outlineObjects: false },
                            tonemapping: { exposure: 0.6 },
                        }),
                    );

                    // Keep an explicit Procore init override so scene-loaded environment/color cannot leak through.
                    // BM-10029 default: flat Light (Legacy) background with engine-default IBL.
                    // Honor the user's previously selected environment (Settings dialog) from local storage so
                    // we don't dispatch setBackground twice (which races the SDK texture loader and produces an
                    // AbortError on refresh).
                    const storedEnvPresetId = getFromStorage(StorageKey.Environment) as EnvironmentPresetId | "";
                    dispatch(
                        renderActions.setBackground(
                            storedEnvPresetId
                                ? getEnvironmentPresetBackground(storedEnvPresetId, config.assetsUrl)
                                : { url: "", color: LIGHT_LEGACY_COLOR },
                        ),
                    );

                    // Terrain rendered as background (non-selectable by default).
                    dispatch(renderActions.setTerrain({ asBackground: true }));

                    // Force performance outline on regardless of device tier.
                    dispatch(renderActions.setDeviceProfile({ features: { outline: true } }));

                    // Restore navigation cube state from storage
                    const storedNavigationCube = getFromStorage(StorageKey.NavigationCube);
                    if (storedNavigationCube !== "") {
                        dispatch(renderActions.setNavigationCube({ enabled: storedNavigationCube === "true" }));
                    }

                    // Point cloud sizing for Procore. A small metric size keeps points from thinning out into
                    // gaps when zoomed in (BM-10530 forced metric to 0, which is what left scans looking
                    // disintegrated).
                    dispatch(
                        renderActions.setPoints({
                            size: { pixel: 1, maxPixel: 20, metric: 0.01, toleranceFactor: 0.4 },
                        }),
                    );
                } else {
                    window.document.title = `${sceneData.title} - Novorender`;
                }

                view.run();

                // First frame after run() is the closest signal we have that the engine
                // drew something. The loading overlay is still up at this point, so this
                // is engine readiness, not what the user can see.
                // Not awaited: the setup below must not wait on a frame.
                void nextFrame()
                    .then(() =>
                        session.markFirstRender({
                            "device.gpu_tier": detectedTier.tier,
                            "device.is_mobile": isMobile,
                        }),
                    )
                    // Without this, a telemetry bug surfaces as an unhandled rejection,
                    // which the global handler reports as an application error.
                    .catch((e) => console.warn("Failed to record time to first render:", e));

                if (!sceneCamera && db && isVersionComparison) {
                    // Don't await - let it load in the background
                    setupVersionComparisonPropertyTree(db, view, pipelineConfig?.version, backendProvider);
                }

                if (viewpoint) {
                    // For loading initial bookmark we use different approach - useHandleInitialBookmark,
                    // but I think we can speed up initial viewpoint loading by putting loading right into useHandleInit, because
                    // - can load viewpoint data in parallel with scene loading
                    // - viewpoint overrides groups, so no need to load IDs for the irrelevant groups
                    try {
                        // Wait for globals and groups to update.
                        // At the first call setViewpoint might not yet have webviewer and view,
                        // so retry a few times with interval.
                        // Use ref of setViewpoint so it uses the latest state of the globals.
                        // I don't like it personally and I've tried to pass the deps manually,
                        // but then there are deps of deps (e.g. webviewer.db) which are not ready yet as well.
                        // There must be a better way to do this, but this one is the simplest.
                        const maxRetries = 10;
                        for (let i = 0; i < maxRetries; i++) {
                            try {
                                await setViewpointRef.current(viewpoint, "system", {
                                    setAsInitial: true,
                                    hidePdfs: backendProvider === "procore",
                                });
                                break;
                            } catch (e) {
                                if (e instanceof ViewpointError && !e.ready && i < maxRetries - 1) {
                                    await sleep(20);
                                } else {
                                    throw e;
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Error setting viewpoint:", e);
                        // If failed to load viewpoint groups - init home groups
                        await fillGroupIds(
                            groups.filter((g) => g.status !== GroupStatus.None),
                            sceneId,
                        );
                    }
                }

                await sleep(2000);

                // Track BIM model opened event for Procore backend
                if (backendProvider === "procore") {
                    void track(Events.bimModel.opened);
                }

                dispatch(
                    renderActions.setSceneStatus({
                        status: AsyncStatus.Success,
                        data: undefined,
                    }),
                );
                session.succeed();
            } catch (e) {
                console.warn(e);
                const classification = classifyFailureForTelemetry(e, {
                    t,
                    isOnline: navigator.onLine,
                    canvas,
                });

                try {
                    if (e instanceof InitCanvasError && gpuGuidanceEnabledRef.current) {
                        // Situation A is classified from the sync WebGL2 probe, so this
                        // resolves without waiting on WebGPU / detect-gpu. A timeout would
                        // only delay session.fail and allow a blue-card flash.
                        await ensureGuidanceSnapshot(true);
                    }
                } catch (guidanceError) {
                    console.error("[GPU guidance] Failed to classify init-canvas error.", guidanceError);
                } finally {
                    session.fail(classification);
                    dispatch(
                        renderActions.setSceneStatus({
                            status: AsyncStatus.Error,
                            msg: classification.failure.errorKind,
                            stack: classification.stack,
                        }),
                    );
                }
            }
        }
    }, [
        canvas,
        dispatch,
        sceneId,
        dispatchGlobals,
        dispatchObjectGroups,
        dispatchHighlighted,
        dispatchHidden,
        dispatchHighlightCollections,
        getProject,
        getModelTreePermissions,
        checkPermissions,
        config,
        fillGroupIds,
        setupVersionComparisonPropertyTree,
        t,
        forcedUxFlavor,
        backendProvider,
        procoreContext,
        hideSpacesRooms,
        internalEventEmitter,
        setViewpoint,
        getViewpointQuery,
        getModelRevisionsQuery,
        viewpointId,
        homeViewpointId,
        initViewpoint,
        store,
    ]);
}

export { getStoredDeviceProfile } from "../debugDeviceProfile";

export async function loadDeviceTier(): Promise<{
    tier: -1 | DeviceProfile["tier"];
    isMobile: boolean;
    gpuName: string | undefined;
}> {
    try {
        const tierResult = await getGPUTier({
            mobileTiers: [0, 70, 90, 300],
            desktopTiers: [0, 50, 75, 300],
        });
        const isApple = tierResult.device?.includes("apple") || false;
        const { isMobile, ...gpuTier } = tierResult;
        let { tier } = gpuTier;

        // Cap Apple handhelds at tier 1: higher tiers' MSAA/resolution/detail inflate
        // memory past WKWebView jetsam limits (~1.5-2GB). Tier 0 stays allowed. (BM-13046)
        if (isMobile && isApple) {
            tier = Math.min(1, tier);
        }

        // All RTX cards belong in tier 2 or higher, but some such as A3000 are not benchmarked and return tier 1.
        if (gpuTier.gpu && /RTX/gi.test(gpuTier.gpu)) {
            tier = Math.max(2, tier);
        }

        // Intel HD Graphics are given tier 2 for some reason, but belonging in tier 0.
        // " " before HD in regex to not catch UHD
        if (gpuTier.gpu && / HD Graphics /gi.test(gpuTier.gpu)) {
            tier = 0;
        }

        if (gpuTier.gpu && /Quadro/gi.test(gpuTier.gpu)) {
            tier = Math.max(2, tier);
        }

        localStorage.setItem("originalDeviceTier", String(tier));

        return {
            tier: tier as DeviceProfile["tier"],
            isMobile: isMobile ?? false,
            gpuName: gpuTier.gpu ?? undefined,
        };
    } catch (e) {
        console.warn(e);
        telemetry.trackCaughtException({ source: "render.useHandleInit.loadDeviceTier", category: "init", error: e });
        return {
            tier: -1 as -1 | DeviceProfile["tier"],
            isMobile: false,
            gpuName: undefined,
        };
    }
}

async function createView(
    canvas: HTMLCanvasElement,
    options?: {
        deviceProfile?: DeviceProfile;
        showWatermark?: boolean;
        invertFlight?: boolean;
        urlBuilder?: (baseUrl: string, filePath: string) => URL | Promise<URL>;
    },
): Promise<View> {
    const deviceProfile = options?.deviceProfile ?? getDeviceProfile(0);
    const view = await View.create(
        canvas,
        deviceProfile,
        (input, pick, conversions) => {
            const flight = new (spaceMouseFlightControllerClass())(input, pick, conversions);
            flight.rotationalInversion = !!options?.invertFlight;
            return {
                ...builtinControllers(input, pick, conversions),
                flight,
            };
        },
        {
            showWatermark: options?.showWatermark,
            webglContextAttributes: { failIfMajorPerformanceCaveat: false },
            urlBuilder: options?.urlBuilder,
        },
    );
    view.controllers.flight.input.mouseMoveSensitivity = 4;
    view.controllers.flight.input.disableWheelOnShift = true;

    // Enable render state debugging if explicitly enabled (disabled by default)
    // Set via console: window.setRenderDebugMode(true) then reload page
    const debugEnabled =
        localStorage.getItem("__RENDER_DEBUG_MODE__") === "true" ||
        (window as unknown as { __RENDER_DEBUG_MODE__?: boolean }).__RENDER_DEBUG_MODE__ === true;
    if (debugEnabled) {
        wrapViewForDebugging(view, true);
    }

    return view;
}

function isWeakDevice() {
    const canvas = document.createElement("canvas");
    try {
        const gl = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true });
        return !gl;
    } finally {
        canvas.remove();
    }
}

async function getAvailableAndForbiddenObjectIds(
    db: ObjectDB,
    availableResourcePaths: string[],
): Promise<undefined | { availableIds: SelectionSet; forbiddenIds: Uint32Array }> {
    // Remove child routes, because parent access implies access to children as well
    availableResourcePaths = availableResourcePaths.filter(
        (p1) => !availableResourcePaths.some((p2) => p2.length > p1.length && p2.startsWith(p1)),
    );

    const availableIds = new Set<ObjectId>();
    const descendantMap = new Map<ObjectId, ObjectId[]>();

    // Get all files/folders, their parents and descendants and make a set of IDs that are available.
    // This set can be used later to filter out e.g. search results.
    // NB: I've tried to search all paths at once with a single multivalue search, but it was much slower
    await Promise.all(
        availableResourcePaths.map(async (path) => {
            const [[obj]] = await iterateAsync({
                iterator: db.search({ parentPath: path, descentDepth: 0 }, undefined),
                count: 1,
            });

            if (!obj) {
                return;
            }
            availableIds.add(obj.id);

            await Promise.all([
                path.includes("/") &&
                    iterateAsync({
                        iterator: db.search({ parentPath: path, descentDepth: -100 }, undefined),
                        count: 100,
                    }).then(([parents]) => {
                        for (const parent of parents) {
                            availableIds.add(parent.id);
                        }
                    }),
                db
                    .descendants(obj, undefined)
                    .then((descendants) => {
                        for (const id of descendants) {
                            availableIds.add(id);
                        }
                        descendantMap.set(obj.id, descendants);
                    })
                    .catch((_) => undefined),
            ]);
        }),
    );

    // Fetch all root objects and corresponding descendants - this should cover all the object IDs available in the project -
    // and make a list of forbidden IDs, which is going to be passed to the highlights as the last group.
    // That's the best way (as opposed to using available IDs) of filtering.
    const forbiddenIds = new Set<ObjectId>();
    const [rootObjects] = await iterateAsync({
        iterator: db.search({ parentPath: "", descentDepth: 1 }, undefined),
        count: 500,
    });

    await Promise.all(
        rootObjects.map(async (obj) => {
            if (!availableIds.has(obj.id)) {
                forbiddenIds.add(obj.id);
            }

            const descendants = descendantMap.get(obj.id) ?? (await db.descendants(obj, undefined).catch((_) => []));
            for (const id of descendants) {
                if (!availableIds.has(id)) {
                    forbiddenIds.add(id);
                }
            }
        }),
    );

    if (forbiddenIds.size === 0) {
        return;
    }

    // The native Sets above are transient (fast adds during construction);
    // the long-lived representations are 4-bytes-per-id sorted arrays.
    return {
        availableIds: SelectionSet.fromIds(availableIds),
        forbiddenIds: new Uint32Array(forbiddenIds).sort(),
    };
}

function getInitUxFlavor(configUxFlavor: string | undefined) {
    if (configUxFlavor && Object.values(UxFlavor).includes(configUxFlavor as UxFlavor)) {
        return configUxFlavor as UxFlavor;
    }

    return localStorage.getItem(newDesignLocalStorageKey) === "false" ? UxFlavor.Classic : UxFlavor.New;
}

/** Runs `fn` inside a telemetry phase span, ensuring the span is always ended. */
async function runPhase<T>(parentSpan: Span | null, name: string, fn: () => Promise<T>): Promise<T> {
    const phase = telemetry.startPhase(parentSpan, name);
    try {
        const result = await fn();
        telemetry.endPhase(phase);
        return result;
    } catch (e) {
        telemetry.endPhase(phase, false, e instanceof Error ? e : undefined);
        throw e;
    }
}
